import { enqueueChatMessageAndWait } from '@platform/chat';
import { chatMessageEntrypoint } from '@platform/chat/messageEntrypoint';
import { removeEntity } from '@platform/timeline';
import { Button, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { registerWidgetRenderer } from '../registry';
import type { RunLimitEntity } from '../types';

export function RunLimitWidget({ entity }: { entity: RunLimitEntity }) {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Require profileSlug to avoid silently switching profiles
  const { profileSlug, convId, maxIterations, iterations, summary } = entity.props || {};
  const hasConv = typeof convId === 'string' && convId.trim().length > 0;
  const hasProfileSlug = typeof profileSlug === 'string' && profileSlug.trim().length > 0;
  const canContinue = hasConv && hasProfileSlug;

  async function onContinue() {
    if (!canContinue || busy) return;
    try {
      setBusy(true);
      setError(null);
      await (dispatch as any)(
        enqueueChatMessageAndWait({
          convId: convId!,
          profile: profileSlug!,
          text: 'Please continue from where you left off.',
          messageEntrypoint: chatMessageEntrypoint.widgetAction,
        })
      );
      // Only remove entity on successful send
      dispatch(removeEntity({ convId: convId!, entityId: entity.id }));
    } catch (err) {
      // Send failed - keep widget visible so user can retry
      const errorMsg = err instanceof Error ? err.message : 'Failed to send continue request';
      setError(errorMsg);
    } finally {
      setBusy(false);
    }
  }

  function onDismiss() {
    if (!hasConv || busy) return;
    dispatch(removeEntity({ convId: convId!, entityId: entity.id }));
  }

  // Build informative title and description
  const title = summary || 'Iteration limit reached';
  const limitInfo =
    typeof maxIterations === 'number' && typeof iterations === 'number'
      ? `Used ${iterations} of ${maxIterations} allowed iterations`
      : typeof maxIterations === 'number'
        ? `Reached limit of ${maxIterations} iterations`
        : typeof iterations === 'number'
          ? `Used ${iterations} iterations`
          : null;

  return (
    <Paper variant="outlined" className="p-3 bg-amber-50 border border-amber-200">
      <Typography variant="subtitle2">{title}</Typography>
      {limitInfo && (
        <Typography variant="body2" color="text.secondary" className="mt-1">
          {limitInfo}
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error" className="mt-2">
          ⚠️ {error}
        </Typography>
      )}
      {!hasProfileSlug && (
        <Typography variant="body2" color="text.secondary" className="mt-2">
          Cannot continue: profile information is missing.
        </Typography>
      )}
      <div className="mt-2 flex gap-2">
        <Button
          size="small"
          variant="contained"
          disabled={!canContinue || busy}
          onClick={onContinue}
        >
          {busy ? 'Continuing...' : 'Continue anyway'}
        </Button>
        <Button size="small" variant="text" disabled={!hasConv || busy} onClick={onDismiss}>
          Stop
        </Button>
      </div>
    </Paper>
  );
}

registerWidgetRenderer(
  'run_limit',
  ({ entity }) => <RunLimitWidget key={(entity as any).id} entity={entity as RunLimitEntity} />,
  { visibility: { normal: true, debug: true } }
);
